import { Button, Divider, Modal, Tooltip } from "@mantine/core";
import { IconInfoSquareRounded } from "@tabler/icons-react";
import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { InvoiceApi } from "../../../apis";
import { PageLayout } from "../../../components/layout";
import { PaymentTermForm } from "../../../components/payment-term/form";
import { PopoverText } from "../../../components/popover-text/popover-text";
import { ColumnType, ITableRef, Table } from "../../../components/table";
import { Tag } from "../../../components/tags";
import RenderText from "../../../components/ui/render-text";
import { IGeneral } from "../../../interfaces/IGeneral";
import { IPaymentTerm } from "../../../interfaces/IPaymentTerm";
import { IReference } from "../../../models/General";

export function ReferencePaymentTermList() {
  const params = useParams();
  const ref = useRef<ITableRef>(null);
  const [action, setAction] = React.useState<string[]>([]);
  const [filters] = React.useState({ condition: `${params?.type}`, configType: "", query: "", isActive: "", isOpen: "", isMain: "" });
  const { paymentTermConditions, paymentTermConfigTypes } = useSelector((state: { general: IGeneral }) => state.general);

  const title = (val: string) => {
    switch (val) {
      case "COD": {
        return "Бэлэн төлөх нөхцөл";
      }
      case "INV_CONFIG": {
        return "Дараа төлөх нөхцөл";
      }
      case "AR_NET30": {
        return "Авлага";
      }
    }
  };

  const subTitle = (val: string) => {
    switch (val) {
      case "COD": {
        return "Бэлэн төлөх нөхцөлийн жагсаалт";
      }
      case "INV_CONFIG": {
        return "Дараа төлөх нөхцөлийн жагсаалт";
      }
      case "AR_NET30": {
        return "Авлага жагсаалт";
      }
    }
  };

  const breadcrumbs = useBreadcrumb(title(params?.type as string));

  const columns = useHeader({
    paymentTermConfigTypes,
    paymentTermConditions,
    onClick: (key, record) => {
      switch (key) {
        case "detail": {
          setAction(["detail", record as any]);
          break;
        }
      }
    },
  });

  const reload = async () => {
    ref.current?.reload();
  };

  return (
    <>
      <PageLayout
        title={`${title(params?.type as string)}`}
        subTitle={`${subTitle(params?.type as string)}`}
        breadcrumb={breadcrumbs}
        extra={[
          <Button
            key={1}
            onClick={() => {
              setAction(["new"]);
            }}
            disabled={params.type === "AR_NET30"}>
            Нэмэх
          </Button>,
        ]}>
        <Divider color="#e5e5e5" my="md" />
        <Table ref={ref} name="partner-management-partner-admin's.list" columns={columns} filters={filters} loadData={(data) => InvoiceApi.list(data!)} />
      </PageLayout>
      <Modal opened={action[0] === "new" || action[0] === "detail"} onClose={() => setAction([])} size="60%" withCloseButton={false} centered>
        <PaymentTermForm action={action} setAction={setAction} reload={reload} />
      </Modal>
    </>
  );
}

type HeaderProps = {
  onClick: (key: string, record: IPaymentTerm) => void;
  paymentTermConfigTypes: IReference[];
  paymentTermConditions: IReference[];
};

const useHeader = ({ onClick, paymentTermConfigTypes, paymentTermConditions }: HeaderProps): ColumnType<IPaymentTerm>[] => [
  {
    title: "#",
    width: "30px",
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: "Үйлдэл",
    align: "left",
    render: (record) => {
      return (
        <Tooltip label="Дэлгэрэнгүй" position="top">
          <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("detail", record)}>
            <IconInfoSquareRounded />
          </Button>
        </Tooltip>
      );
    },
  },
  {
    title: "Код",
    sorter: true,
    dataIndex: "refCode",
    render: (record) => <RenderText text={record?.refCode} />,
  },
  {
    title: "Нөхцөл",
    sorter: true,
    dataIndex: "familyName",
    render: (record) => <RenderText text={paymentTermConditions?.find((c) => c?.code === record?.condition)?.name as string} />,
  },
  {
    title: "Захиалга баталгаажих",
    sorter: true,
    dataIndex: "email",
    render: (record) => <RenderText text={paymentTermConfigTypes?.find((c) => c?.code === record?.configType)?.text ?? "-"} />,
  },
  {
    title: "Тайлбар",
    sorter: true,
    dataIndex: "description",
    render: (record) => <PopoverText>{record?.description || ""}</PopoverText>,
  },
  {
    title: "Expire day count",
    sorter: true,
    dataIndex: "firstName",
    render: (record) => <RenderText text={record?.expireDayCount || "-"} />,
  },
  {
    title: "Confirmation day",
    sorter: true,
    dataIndex: "registerNo",
    render: (record) => <RenderText text={record?.confirmationDay || "-"} />,
  },
  {
    title: "Payment day",
    sorter: true,
    dataIndex: "departmentUnit",
    render: (record) => <RenderText text={record?.paymentDay || "-"} />,
  },
  {
    title: "DeHUB - Public",
    sorter: true,
    dataIndex: "isOpen",
    render: (record) => <Tag name={record?.isOpen ? "Тийм" : "Үгүй"} color={record.isOpen ? "green.5" : "red.5"} />,
  },
  {
    title: "Үндсэн эсэх",
    sorter: true,
    dataIndex: "isMain",
    render: (record) => <Tag name={record.isMain ? "Тийм" : "Үгүй"} color={record.isMain ? "green.5" : "red.5"} />,
  },
  {
    title: "Идэвхтэй эсэх",
    sorter: true,
    dataIndex: "isActive",
    render: (record) => <Tag name={record.isActive ? "Тийм" : "Үгүй"} color={record.isActive ? "green.5" : "red.5"} />,
  },
];

const useBreadcrumb = (type: any) => [
  {
    to: "/",
    label: "Эхлэл",
  },
  {
    to: "/reference/b2b-list",
    label: "Төлбөрийн нөхцөл",
  },
  {
    label: type,
  },
];
